import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import omitBy from 'lodash/omitBy';
import qs from 'qs';

type SearchParams = Record<string, string>;

export const useSearchParamsWithValues = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useMemo(
    () =>
      location.search
        ? (qs.parse(location.search, {
            ignoreQueryPrefix: true,
          }) as SearchParams)
        : {},
    [location.search],
  );

  const setSearchParams = useCallback(
    (newValues: SearchParams) => {
      const searchPath = qs.stringify(
        omitBy(newValues, v => !v),
        { arrayFormat: 'comma', strictNullHandling: true },
      );

      const hasSearch = Boolean(searchPath);
      const nextUrl = hasSearch
        ? `${location.pathname}?${searchPath}`
        : location.pathname;

      navigate(nextUrl, { replace: true });
    },
    [navigate, location.pathname],
  );

  const updateSearchParams = useCallback(
    (newValues: SearchParams) =>
      setSearchParams({ ...searchParams, ...newValues }),
    [searchParams, setSearchParams],
  );

  return {
    searchParams,
    setSearchParams,
    updateSearchParams,
  };
};
